import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DaySelectorComponent } from '../day-selector/day-selector.component';
import { DailyReportTableComponent } from '../daily-report-table/daily-report-table.component';
import { AuthService } from '../../../services/auth.service';
import { DailyReportService } from '../../../services/daily-report.service';
import { DATA_START_DATE, DailyReportCell, DayInfo } from '../../../interfaces/daily-report.interface';

@Component({
  selector: 'app-daily-report-wrapper',
  standalone: true,
  imports: [
    MatCardModule,
    MatIconModule,
    DaySelectorComponent,
    DailyReportTableComponent
  ],
  templateUrl: './daily-report-wrapper.component.html',
  styleUrls: ['./daily-report-wrapper.component.scss']
})
export class DailyReportWrapperComponent implements OnDestroy {
  selectedDay: DayInfo | null = null;
  callCenterUsers: any[] = [];
  cellsByUser: { [userId: number]: DailyReportCell[] } = {};
  allCells: DailyReportCell[] = [];
  isLoading = true;
  isExporting = false;
  hasNoData = false;
  hasLoadedOnce = false;

  private selectedDate: string | null = null;
  private readonly cancelRequest$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private dailyReportService: DailyReportService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.cancelRequest$.next();
    this.cancelRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDayChanged(day: DayInfo): void {
    const reportDate = this.formatDate(day.date);
    if (reportDate === this.selectedDate) {
      return;
    }

    this.selectedDate = reportDate;
    this.selectedDay = day;
    this.cancelRequest$.next();

    // Hétvégén és az adatgyűjtés kezdete előtt nincs értelme lekérdezni
    if (day.isWeekend || reportDate < DATA_START_DATE) {
      this.hasNoData = reportDate < DATA_START_DATE;
      this.isLoading = false;
      this.callCenterUsers = [];
      this.cellsByUser = {};
      this.allCells = [];
      this.cdr.detectChanges();
      return;
    }

    this.hasNoData = false;
    this.loadUsers();
  }

  isWeekend(): boolean {
    return this.selectedDay?.isWeekend ?? false;
  }

  showSummaryTable(): boolean {
    return this.callCenterUsers.length > 1 && (this.authService.isAdmin() || this.authService.isManager());
  }

  isExportDisabled(): boolean {
    return this.isWeekend() || this.hasNoData || this.isLoading || this.callCenterUsers.length === 0;
  }

  onExportRequested(): void {
    if (!this.selectedDate || this.isExportDisabled()) {
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    const reportDate = this.selectedDate;

    // Szándékosan csak a destroy$ szakítja meg: napváltásnál se szakadjon félbe a letöltés
    this.dailyReportService.exportReport({
      report_date: reportDate,
      user_ids: this.callCenterUsers.map(user => user.id)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          this.downloadReportFile(blob, reportDate);
          this.isExporting = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.snackBar.open('A napi riport exportálása sikertelen', 'OK', { duration: 4000 });
          this.isExporting = false;
          this.cdr.detectChanges();
        }
      });
  }

  private downloadReportFile(blob: Blob, reportDate: string): void {
    const fileName = `Napi_riport_${reportDate.replace(/-/g, '')}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private loadUsers(): void {
    this.isLoading = true;

    const currentUser = this.authService.getCurrentUser();

    // Call Center munkatárs csak a saját riportját látja
    if (this.authService.isCallCenterEmployee() && currentUser) {
      this.loadReport([currentUser]);
      return;
    }

    this.authService.getAllUsers()
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (users) => {
          this.loadReport(users.filter(user => user.jobs?.includes('call_center')));
        },
        error: () => {
          this.callCenterUsers = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private loadReport(users: any[]): void {
    if (!this.selectedDay || users.length === 0) {
      this.callCenterUsers = [];
      this.cellsByUser = {};
      this.allCells = [];
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.dailyReportService.getBulkReport({
      report_date: this.formatDate(this.selectedDay.date),
      user_ids: users.map(user => user.id)
    })
      .pipe(takeUntil(this.cancelRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (cells) => {
          this.allCells = cells;
          this.cellsByUser = {};
          for (const cell of cells) {
            (this.cellsByUser[cell.user_id] ??= []).push(cell);
          }

          // Inaktív munkatárs csak akkor kap táblázatot, ha van adata az adott napon
          this.callCenterUsers = users.filter(user => user.is_active || this.cellsByUser[user.id]?.length);
          this.isLoading = false;
          this.hasLoadedOnce = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.callCenterUsers = [];
          this.allCells = [];
          this.cellsByUser = {};
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private formatDate(date: Date): string {
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
}
